import { useCallback, useMemo } from "react"
import { SearchDetailResult } from "interface/search"
import { SisetuResult, useListSisetu } from "interface/sisetu"
import { Grid, useGrid } from "components/grid"
import { KojinOrgan, KojinResult, useListKojin } from "interface/kojin"
import { useMixedGridColumns, SisetuWithKolResult } from "./columns"

type MixedPageProps = {
	search: SearchDetailResult
} & ReturnType<typeof useGrid>
export const MixedPage = ({
	search,
	...others
}: MixedPageProps) => {
	const { columnsDef } = useMixedGridColumns({ search })

	const onFetchedSisetuList = useCallback(
		(result: SisetuResult[]) => {
			result.sort((a, b) => {
				if (a.score?.rank && b.score?.rank) {
					return a.score.rank - b.score.rank
				}
				if (a.sisetuNameKana && b.sisetuNameKana) {
					return a.sisetuNameKana.localeCompare(b.sisetuNameKana)
				}
				return 0
			})
		},
		[]
	)
	const sisetuListState = useListSisetu(search.searchId, undefined, {
		updateTime: search.updateTime,
		rankLimit: others.options.sisetuLimit,
		onFetched: onFetchedSisetuList
	})

	const onFetchedKojinList = useCallback(
		(result: KojinResult[]) => {
			result.sort((a, b) => {
				if (a.score?.rank && b.score?.rank) {
					return a.score.rank - b.score.rank
				}
				if (a.isiNameKana && b.isiNameKana) {
					return a.isiNameKana.localeCompare(b.isiNameKana)
				}
				return 0
			})
		},
		[]
	)
	const kojinListState = useListKojin(search.searchId, undefined, {
		updateTime: search.updateTime,
		rankLimit: others.options.kojinLimit,
		onFetched: onFetchedKojinList
	})

	const progress = useMemo(
		() => {
			if (sisetuListState.loading || kojinListState.loading) {
				const sisetuProgress = sisetuListState.loading ? (
					(sisetuListState.loaded && sisetuListState.total) ? (
						(sisetuListState.loaded / sisetuListState.total) * 100
					) : 0
				) : 100
				const kojinProgress = kojinListState.loading ? (
					(kojinListState.loaded && kojinListState.total) ? (
						(kojinListState.loaded / kojinListState.total) * 100
					) : 0
				) : 100
				console.log(`${kojinProgress} + ${sisetuProgress}`)
				return (sisetuProgress + kojinProgress) / 2
			}
			return undefined
		},
		[sisetuListState, kojinListState]
	)
	const dataList = useMemo<SisetuWithKolResult[]>(
		() => {
			if (!sisetuListState.data || !kojinListState.data) return []
			const sisetuMap: { [sisetuCode: string]: { sisetu: SisetuResult, kolList: { kojin: KojinResult, organ: KojinOrgan }[] }} = {}
			sisetuListState.data.forEach(sisetu => {
				sisetuMap[sisetu.sisetuCode] = { sisetu, kolList: [] }
			})
			kojinListState.data.forEach(kojin => {
				kojin.organ?.forEach(organ => {
					const sisetu = sisetuMap[organ.sisetuCode]
					if (sisetu) sisetu.kolList.push({ kojin, organ })
				})
			})
			const sisetuList = Object.values(sisetuMap)
			sisetuList.sort((a, b) => (a.sisetu.score?.rank ?? 0) - (b.sisetu.score?.rank ?? 0))
			return sisetuList.flatMap(
				({ sisetu, kolList }) => {
					if (kolList.length === 0) return { sisetu }
					return kolList.map(({ kojin, organ }) => ({ sisetu, kojin, organ }))
				}
			)
		},
		[sisetuListState, kojinListState]
	)
	return (
		<Grid
			{...others}
			saveKey="mixed_grid"
			baseFileName={search.title}
			sheetName="施設xKOL"
			loading={sisetuListState.loading || kojinListState.loading}
			progress={progress}
			columnsDef={columnsDef}
			dataList={dataList}
		/>
	)
}